import React, { useEffect, useRef, useState } from "react";
import { IconChevronDown, IconSearch, IconX, IconList, IconCalendar } from "@/lib/icons";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { displayName } from "@/lib/patientFormat";
import { Seg } from "./thUi";
import { RANGES, FAINT } from "./thModel";

export function ProviderPick({ providers, value, onChange, inGroup }) {
  const label = !value.length ? "All providers" : value.length === 1 ? displayName(providers.find((p) => p.npi === value[0])?.name || value[0]) : `${value.length} providers`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><button type="button" className={inGroup ? "th-cell" : "th-pill"} data-testid="th-provider">{label}<IconChevronDown size={14} /></button></DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[240px]" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DropdownMenuCheckboxItem checked={!value.length} onSelect={(e) => { e.preventDefault(); onChange([]); }} data-testid="th-provider-all">All providers</DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {providers.map((p) => (
          <DropdownMenuCheckboxItem key={p.npi} checked={value.includes(p.npi)} onSelect={(e) => { e.preventDefault(); onChange(value.includes(p.npi) ? value.filter((x) => x !== p.npi) : [...value, p.npi]); }} data-testid={`th-provider-${p.npi}`}>
            <div><div className="text-[13px]">{displayName(p.name)}</div><div className="text-[11px]" style={{ color: FAINT }}>NPI {p.npi}</div></div>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RangePick({ value, onChange, inGroup }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><button type="button" className={inGroup ? "th-cell" : "th-pill"} data-testid="th-range">{RANGES.find((r) => r[0] === value)[1]}<IconChevronDown size={14} /></button></DropdownMenuTrigger>
      <DropdownMenuContent align="start" onCloseAutoFocus={(e) => e.preventDefault()}>{RANGES.map(([k, l]) => <DropdownMenuItem key={k} onSelect={() => onChange(k)} data-testid={`th-range-${k}`}>{l}</DropdownMenuItem>)}</DropdownMenuContent>
    </DropdownMenu>
  );
}

function SearchBox({ value, onChange, inGroup }) {
  const [v, setV] = useState(value);
  const [open, setOpen] = useState(!!value);
  const inRef = useRef(null);
  useEffect(() => { const t = setTimeout(() => { if (v.trim() !== value) onChange(v.trim()); }, 300); return () => clearTimeout(t); }, [v]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) inRef.current?.focus(); }, [open]);
  useEffect(() => {
    const h = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const a = document.activeElement, tag = a?.tagName || "";
      if (/input|textarea|select/i.test(tag) || a?.isContentEditable) return;
      e.preventDefault(); setOpen(true);
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, []);
  if (!open) {
    return (
      <button type="button" className={inGroup ? "th-cell" : "th-pill"} style={{ width: 36, padding: 0, justifyContent: "center", cursor: "pointer" }}
        title="Search · Patient or MRN" aria-label="Search" data-testid="th-search-open" onClick={() => setOpen(true)}>
        <IconSearch size={15} style={{ color: FAINT }} />
      </button>
    );
  }
  return (
    <label className={inGroup ? "th-cell" : "th-pill"} style={{ minWidth: 200, cursor: "text" }} data-testid="th-search-cell">
      <IconSearch size={14} style={{ color: FAINT }} />
      <input ref={inRef} value={v} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") { if (v) { setV(""); onChange(""); } else setOpen(false); } }}
        onBlur={() => { if (!v.trim()) setOpen(false); }}
        placeholder="Patient or MRN" className="flex-1 min-w-0 outline-none bg-transparent" data-testid="th-search" />
      {v ? <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setV(""); onChange(""); inRef.current?.focus(); }} aria-label="Clear search" data-testid="th-search-clear"><IconX size={13} /></button> : null}
    </label>
  );
}

export function Toolbar({ f, set, counts, providers, weekNav }) {
  return (
    <div className="flex flex-wrap items-center gap-2.5 p-4 border-b" style={{ borderColor: "#E9E9EC" }} data-testid="th-toolbar">
      <Seg testid="th-seg" onChange={(seg) => set({ seg })} value={f.seg} items={[["open", "Open", counts.open], ["waiting", "Waiting room", counts.waiting, counts.waiting > 0], ["completed", "Completed", counts.completed], ["cancelled", "Cancelled & no-show", counts.cancelled], ["all", "All", counts.all]]} />
      <span style={{ flex: 1 }} aria-hidden="true" />
      <div className="th-tg" data-testid="th-toolgroup">
        <div className="th-tg-part">{f.view === "list" ? <RangePick value={f.range} onChange={(range) => set({ range })} inGroup /> : <span className="px-2">{weekNav}</span>}</div>
        <div className="th-tg-part"><ProviderPick providers={providers} value={f.npis} onChange={(npis) => set({ npis })} inGroup /></div>
        <div className="th-tg-part"><SearchBox value={f.q} onChange={(q) => set({ q })} inGroup /></div>
      </div>
      <div className="th-seg th-seg-icons" role="group" data-testid="th-view">
        <button type="button" aria-pressed={f.view === "list"} aria-label="List view" title="List view" onClick={() => set({ view: "list" })} data-testid="th-view-list"><IconList size={15} /></button>
        <button type="button" aria-pressed={f.view === "week"} aria-label="Week view" title="Week view" onClick={() => set({ view: "week" })} data-testid="th-view-week"><IconCalendar size={15} /></button>
      </div>
    </div>
  );
}
